<template>
  <b-modal
    v-model="show"
    body-bg-variant="transparent"
    :body-class="kind === 'pdf' ? 'p-0' : 'px-0'"
    hide-footer
    hide-header
    centered
    :size="kind === 'pdf' ? 'xl' : 'lg'"
    modal-class="file-preview-modal"
    data-test="file-preview-modal"
  >
    <div v-if="allowDownload" class="preview-toolbar">
      <b-button
        size="sm"
        variant="light"
        class="preview-download-btn"
        :disabled="isDownloading"
        data-test="preview-download"
        @click="download"
      >
        <b-icon icon="download" class="mr-1" />
        {{ isDownloading ? 'Downloading…' : 'Download' }}
      </b-button>
    </div>

    <div v-if="kind === 'pdf'" class="pdf-resize-wrapper">
      <iframe
        :src="url"
        class="pdf-preview"
        frameborder="0"
        data-test="preview-pdf"
      ></iframe>
    </div>

    <!-- CSV/XLSX: real content fetched through the API and parsed client-side,
         falling back to the file-type icon if the fetch/parse fails. -->
    <div
      v-else-if="kind === 'spreadsheet'"
      class="csv-preview-wrapper"
      data-test="preview-spreadsheet"
    >
      <div v-if="spreadsheetLoading" class="text-center py-5">
        <digger-loader />
      </div>
      <div
        v-else-if="spreadsheetRows && spreadsheetRows.length"
        class="csv-preview-table-scroll"
      >
        <table class="csv-preview-table">
          <tbody>
            <tr
              v-for="(row, rIndex) in spreadsheetRows"
              :key="rIndex"
              :class="{ 'csv-preview-header': rIndex === 0 }"
            >
              <td v-for="(cell, cIndex) in row" :key="cIndex">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="non-previewable-preview">
        <b-img :src="docIcon" class="non-previewable-icon" />
        <p class="mt-3 mb-0">This file type can't be previewed.</p>
      </div>
    </div>

    <!-- Word: converted to simple HTML in the browser (see utils/docxPreview) —
         text, headings, lists, tables, links and pictures, not the exact page
         layout. The HTML has been cleaned to an allow-list before it gets here. -->
    <div
      v-else-if="kind === 'document'"
      class="doc-preview-wrapper"
      data-test="preview-document"
    >
      <div v-if="documentLoading" class="text-center py-5">
        <digger-loader />
      </div>
      <template v-else-if="documentHtml">
        <p class="doc-preview-note" data-test="preview-document-note">
          Simplified view. Download the file to see the exact layout.
        </p>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="doc-preview"
          data-test="preview-document-body"
          v-html="documentHtml"
        ></div>
      </template>
      <div v-else class="non-previewable-preview">
        <b-img :src="docIcon" class="non-previewable-icon" />
        <p class="mt-3 mb-0">This file type can't be previewed.</p>
      </div>
    </div>

    <b-img
      v-else-if="kind === 'image' && !imageFailed"
      class="preview"
      :src="url"
      :alt="name"
      data-test="preview-image"
      @error="imageFailed = true"
    />

    <div v-else class="csv-preview-wrapper">
      <div class="non-previewable-preview" data-test="preview-unavailable">
        <b-img :src="docIcon" class="non-previewable-icon" />
        <p class="mt-3 mb-0">This file type can't be previewed.</p>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  nonImageDocIcon,
  isCsvFile,
  isXlsxFile,
  parseCsvPreviewRows,
  parseXlsxPreviewRows,
} from '@/utils/fileTypeIcons'
import { docxToSafeHtml } from '@/utils/docxPreview'

const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']

function extensionOf(name) {
  const value = typeof name === 'string' ? name.split('?')[0] : ''
  const dot = value.lastIndexOf('.')
  return dot >= 0 ? value.slice(dot + 1).toLowerCase() : ''
}

/**
 * The same preview the tradie file page uses, for anywhere a file would
 * otherwise open in a new tab: PDFs in the browser viewer, images, CSV/XLSX as
 * a table, Word (.docx) as a simplified document, and a plain "can't be
 * previewed" box (with Download) for the rest.
 *
 * Pass `mediaId` (the API media id) or `contentUrl` (an API path) to enable
 * the spreadsheet and Word previews, which read the file through the API.
 * `mediaId` also lets Download save the file under its real name. Without
 * either, Download falls back to a plain link to `url`.
 */
export default {
  name: 'FilePreviewModal',

  props: {
    visible: { type: Boolean, default: false },
    url: { type: String, default: '' },
    mimeType: { type: String, default: '' },
    // Shown as the image alt text and used to work out the type when the
    // mime type is missing or generic.
    name: { type: String, default: '' },
    mediaId: { type: [Number, String], default: null },
    // API path that returns the file's bytes, for files that aren't plain
    // media (e.g. a project file's `projects/7/files/3/content`). Enables the
    // spreadsheet table the same way `mediaId` does.
    contentUrl: { type: String, default: '' },
    // File name to save as. Defaults to `name`.
    downloadName: { type: String, default: '' },
    // Hide the Download button (e.g. the viewer isn't allowed to download).
    allowDownload: { type: Boolean, default: true },
    // The caller already knows how to download this file (and under what
    // name): emit `download` instead of downloading here.
    customDownload: { type: Boolean, default: false },
    // The caller is downloading (shown on the button while it runs).
    busy: { type: Boolean, default: false },
  },

  data() {
    return {
      imageFailed: false,
      downloading: false,
      spreadsheetRows: null,
      spreadsheetLoading: false,
      documentHtml: '',
      documentLoading: false,
    }
  },

  computed: {
    show: {
      get() {
        return this.visible
      },
      set(open) {
        if (!open) this.$emit('close')
      },
    },

    // The shape the spreadsheet helpers expect.
    fileLike() {
      return { name: this.name, mime_type: this.mimeType }
    },

    kind() {
      const ext = extensionOf(this.name)
      if (this.mimeType === 'application/pdf' || ext === 'pdf') return 'pdf'
      // Before the spreadsheet check: a .docx is a zip, and a zip mime type
      // would otherwise be taken for an Excel file.
      if (ext === 'docx' || this.mimeType === DOCX_MIME_TYPE) {
        return this.contentPath ? 'document' : 'other'
      }
      if (isCsvFile(this.fileLike) || isXlsxFile(this.fileLike)) {
        // Rows come from the API, so without somewhere to read them there is
        // nothing to show — the "can't be previewed" box instead.
        return this.contentPath ? 'spreadsheet' : 'other'
      }
      if (
        (this.mimeType || '').indexOf('image/') === 0 ||
        IMAGE_EXTENSIONS.includes(ext)
      ) {
        return 'image'
      }
      // Anything else (Word, zip…) has no in-browser view: the "can't be
      // previewed" box with a Download button.
      return 'other'
    },

    // Where the file's bytes are read from for a spreadsheet table, if anywhere.
    contentPath() {
      if (this.contentUrl) return this.contentUrl
      return this.mediaId ? `media/${this.mediaId}/content` : ''
    },

    docIcon() {
      return (
        nonImageDocIcon(this.fileLike) || require('@/assets/icons/file.svg')
      )
    },

    saveName() {
      return this.downloadName || this.name || 'file'
    },

    isDownloading() {
      return this.downloading || this.busy
    },

    // Changes when the modal opens or moves to a different file, so the
    // per-file state below is reset (and spreadsheet rows re-fetched) each time.
    previewKey() {
      return this.visible
        ? `${this.url}|${this.mediaId}|${this.contentUrl}`
        : null
    },
  },

  watch: {
    previewKey: {
      immediate: true,
      handler(key) {
        this.imageFailed = false
        this.spreadsheetRows = null
        this.documentHtml = ''
        if (key && this.kind === 'spreadsheet') this.loadSpreadsheet()
        if (key && this.kind === 'document') this.loadDocument()
      },
    },
  },

  methods: {
    // A Word file is a zip, so it is read whole (never truncated) and converted
    // in the browser. Anything that goes wrong (network, corrupt file) leaves
    // documentHtml empty, which shows the "can't be previewed" box.
    async loadDocument() {
      const request = (this.documentRequest = (this.documentRequest || 0) + 1)
      this.documentLoading = true
      try {
        const { data } = await this.$api.get(this.contentPath, {
          responseType: 'arraybuffer',
        })
        const html = await docxToSafeHtml(data)
        if (request === this.documentRequest) this.documentHtml = html
      } catch (err) {
        if (request === this.documentRequest) this.documentHtml = ''
      } finally {
        if (request === this.documentRequest) this.documentLoading = false
      }
    },

    async loadSpreadsheet() {
      const request = (this.spreadsheetRequest =
        (this.spreadsheetRequest || 0) + 1)
      this.spreadsheetLoading = true
      try {
        let rows
        if (isXlsxFile(this.fileLike)) {
          const { data } = await this.$api.get(this.contentPath, {
            responseType: 'arraybuffer',
          })
          rows = parseXlsxPreviewRows(data)
        } else {
          const { data: text } = await this.$api.get(this.contentPath, {
            responseType: 'text',
            params: { max_bytes: 16384 },
          })
          rows = parseCsvPreviewRows(text)
        }
        if (request === this.spreadsheetRequest) this.spreadsheetRows = rows
      } catch (err) {
        if (request === this.spreadsheetRequest) this.spreadsheetRows = null
      } finally {
        if (request === this.spreadsheetRequest) {
          this.spreadsheetLoading = false
        }
      }
    },

    // Through the authenticated API when we have a media id, so the file is
    // saved under its real name (the raw URL ends in a storage hash and the
    // bucket has no CORS policy for a browser fetch). Otherwise a plain link.
    async download() {
      if (this.isDownloading) return

      if (this.customDownload) {
        this.$emit('download')
        return
      }

      if (!this.mediaId) {
        // A same-origin or blob URL is saved under `downloadName`; browsers
        // ignore the name for a cross-origin one, which just opens.
        this.openLink(this.url, this.downloadName)
        return
      }

      this.downloading = true
      let objectUrl = null
      try {
        const { data: blob } = await this.$api.get(
          `media/${this.mediaId}/content`,
          { responseType: 'blob' },
        )
        objectUrl = window.URL.createObjectURL(blob)
        this.openLink(objectUrl, this.saveName)
      } catch (err) {
        this.openLink(this.url)
      } finally {
        if (objectUrl) window.URL.revokeObjectURL(objectUrl)
        this.downloading = false
      }
    },

    openLink(href, filename) {
      if (!href) return
      const link = document.createElement('a')
      link.href = href
      if (filename) link.download = filename
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="scss" scoped>
.preview {
  display: block;
  max-width: 100%;
  max-height: 80vh;
  width: auto;
  height: auto;
  margin: 0 auto;
}

// Anchored to the modal content so it floats over any preview type
// (image / PDF iframe / spreadsheet table / Word document).
.preview-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
}

.preview-download-btn {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.non-previewable-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem;
}

.non-previewable-icon {
  width: 96px;
  height: auto;
}

.csv-preview-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
}

.doc-preview-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 1rem 1.5rem 1.5rem;
}

.doc-preview-note {
  margin: 0 0 0.75rem;
  padding-right: 6.5rem; // clear of the floating Download button
  font-size: 0.8rem;
  color: #6b7280;
}

.doc-preview {
  max-height: 70vh;
  overflow: auto;
}

.csv-preview-table-scroll {
  max-width: 100%;
  max-height: 70vh;
  overflow: auto;
}

.csv-preview-table {
  border-collapse: collapse;
  font-size: 0.85rem;
  white-space: nowrap;

  td {
    border: 1px solid #e1e6ea;
    padding: 0.4rem 0.6rem;
  }
}

.csv-preview-header {
  font-weight: 700;
  background: #f5f7f9;
}

.pdf-resize-wrapper {
  resize: both;
  overflow: hidden;
  width: 100%;
  height: 80vh;
  min-width: 300px;
  min-height: 200px;
}

.pdf-preview {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}
</style>

<!-- Not scoped: the converted Word document is inserted with v-html, so its
     elements never get this component's scope attribute. Everything is
     namespaced under .file-preview-modal so nothing else is affected. -->
<style lang="scss">
.file-preview-modal .doc-preview {
  color: #111827;
  line-height: 1.55;
  overflow-wrap: anywhere;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1.1rem 0 0.5rem;
    font-weight: 700;
    line-height: 1.25;
  }

  h1 {
    font-size: 1.5rem;
  }

  h2 {
    font-size: 1.25rem;
  }

  h3,
  h4,
  h5,
  h6 {
    font-size: 1.05rem;
  }

  p {
    margin: 0 0 0.75rem;
  }

  ul,
  ol {
    margin: 0 0 0.75rem;
    padding-left: 1.5rem;
  }

  table {
    border-collapse: collapse;
    margin: 0 0 1rem;
    max-width: 100%;
  }

  td,
  th {
    border: 1px solid #e5e7eb;
    padding: 0.35rem 0.6rem;
    vertical-align: top;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  a {
    color: #006cff;
    text-decoration: underline;
  }

  blockquote {
    margin: 0 0 0.75rem;
    padding-left: 1rem;
    border-left: 3px solid #e5e7eb;
    color: #374151;
  }
}
</style>
